import React from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';

import { AppContextProvider, type Env } from '@contexts/AppContext';
import useEventTracking from '@hooks/useEventTracking';
import type { Newsletter as SignupNewsletter } from '@interfaces/homepage';
import { Page } from '@tracking';
import type { User } from '@utils/backend-api/currentUser';
import type { Newsletter } from '@utils/backend-api/newsletters';
import type { LiveStream, TITVEvent } from '@utils/backend-api/titv';

import DailyShowDescription from '../DailyShowDescription';
import Description from '../Description';
import EventStreamPlayer from '../EventStreamPlayer';
import NewslettersSignup from '../NewslettersSignUp';
import PastEvents from '../PastEvents';
import type { TITVPartnerAd } from '../interfaces';
import type { TITVNetwork as TITVNetworkType } from './interfaces';

interface TITVNetworkProps {
  availableNewsletters?: Newsletter[];
  env: Env;
  isGdprRequired: boolean;
  initialCurrentUser?: User;
  inProgressStream?: LiveStream;
  isWebview: boolean;
  mostRecentStream?: TITVEvent;
  partnerAds?: TITVPartnerAd[];
  signupNewsletter?: SignupNewsletter;
  titvNetwork: TITVNetworkType;
}

const queryClient = new QueryClient();

const DAILY_SHOW_SLUG = 'titv';

const TITVNetwork = ({
  availableNewsletters,
  env,
  isGdprRequired,
  initialCurrentUser,
  inProgressStream,
  isWebview,
  mostRecentStream,
  partnerAds,
  signupNewsletter,
  titvNetwork,
}: TITVNetworkProps) => {
  const { Track } = useEventTracking({
    page: Page.TITVNetwork,
    contentId: titvNetwork.id,
  });

  return (
    <Track>
      <AppContextProvider
        initialCurrentUser={initialCurrentUser}
        isGdprRequired={isGdprRequired}
        isWebview={isWebview}
        env={env}
      >
        <QueryClientProvider client={queryClient}>
          <EventStreamPlayer
            inProgressStream={inProgressStream}
            mostRecentStream={mostRecentStream}
          />
          <div className="mt-72 px-28">
            <div className="m-auto max-w-container-lg">
              {titvNetwork.slug === DAILY_SHOW_SLUG ? (
                <DailyShowDescription partnerAds={partnerAds} signupNewsletter={signupNewsletter} />
              ) : (
                <Description
                  partnerAds={partnerAds}
                  signupNewsletter={signupNewsletter}
                  titvNetwork={titvNetwork}
                />
              )}
              <PastEvents networkId={titvNetwork.id} />
            </div>
          </div>
          {!initialCurrentUser && (
            <NewslettersSignup newsletters={availableNewsletters as Newsletter[]} />
          )}
        </QueryClientProvider>
      </AppContextProvider>
    </Track>
  );
};

export default TITVNetwork;
